export type RabbitHoleTopicRefinement = {
  topic: string;
  suggestions: string[];
  senseLabel?: string;
};

const AMBIGUOUS_RABBIT_HOLE_TOPICS: Record<string, string[]> = {
  maus: [
    "the animal",
    "the computer mouse",
  ],
  techno: [
    "Techno music and club culture",
    "Detroit techno",
    "Minimal techno",
    "Techno festivals and DJs",
  ],
};

const AMBIGUOUS_RABBIT_HOLE_SENSES: Record<string, string> = {
  maus: "the animal",
};

function normalizeRabbitHoleTopic(topic: string): string {
  return topic.trim().toLowerCase().replace(/\s+/g, " ");
}

export function getRabbitHoleTopicRefinement(
  topic: string,
): RabbitHoleTopicRefinement | null {
  const trimmed = topic.trim();
  if (!trimmed) {
    return null;
  }

  const normalized = normalizeRabbitHoleTopic(trimmed);
  const suggestions = AMBIGUOUS_RABBIT_HOLE_TOPICS[normalized];
  if (!suggestions || suggestions.length === 0) {
    return null;
  }

  return {
    topic: trimmed,
    suggestions,
    senseLabel: AMBIGUOUS_RABBIT_HOLE_SENSES[normalized],
  };
}

const WIKIPEDIA_API_BASE = "https://en.wikipedia.org/w/api.php";
const WIKIPEDIA_TIMEOUT_MS = 5000;

function wikipediaApiUrl(params: Record<string, string>): string {
  const searchParams = new URLSearchParams({ format: "json", origin: "*", ...params });
  return `${WIKIPEDIA_API_BASE}?${searchParams.toString()}`;
}

interface WikipediaDisambiguationResult {
  query?: {
    pages?: Record<
      string,
      {
        pageid?: number;
        ns?: number;
        title?: string;
        missing?: string;
      }
    >;
  };
}

async function wikipediaDisambiguationPageExists(
  topic: string,
  signal: AbortSignal,
): Promise<boolean> {
  const url = wikipediaApiUrl({
    action: "query",
    titles: `${topic}_(disambiguation)`,
  });
  const response = await fetch(url, { signal });
  if (!response.ok) return false;
  const data: WikipediaDisambiguationResult = await response.json();
  const pages = data.query?.pages ?? {};
  return Object.values(pages).every((page) => !("missing" in page));
}

interface WikipediaOpenSearchResult {
  0: string;
  1: string[];
  2: string[];
  3: string[];
}

async function wikipediaSearchSuggestions(
  topic: string,
  signal: AbortSignal,
): Promise<string[]> {
  const url = wikipediaApiUrl({
    action: "opensearch",
    search: topic,
    limit: "6",
  });
  const response = await fetch(url, { signal });
  if (!response.ok) return [];
  const data: WikipediaOpenSearchResult = await response.json();
  return (data[1] ?? []).slice(0, 6);
}

export async function fetchWikipediaRefinement(
  topic: string,
): Promise<RabbitHoleTopicRefinement | null> {
  const trimmed = topic.trim();
  if (!trimmed) return null;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), WIKIPEDIA_TIMEOUT_MS);

  try {
    const exists = await wikipediaDisambiguationPageExists(trimmed, controller.signal);
    if (!exists) return null;

    const suggestions = await wikipediaSearchSuggestions(trimmed, controller.signal);
    if (suggestions.length < 2) return null;

    return { topic: trimmed, suggestions };
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}
